import { FunctionComponent, useEffect, useState } from 'react';
import { Bar, BarChart, LabelList, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { ContentBrick } from '../../../../app-kit/components/ContentBrick/ContentBrick';
import styled, { useTheme } from 'styled-components';
import { SubBrickTitle } from './SubBrickTitle';
import { ColorVariant, mapColorVariantToBarStyle } from '../../../../helpers/colorVariant';

type BarGraphBar = {
  barLabel: string;
  value: number;
  colorVariant: ColorVariant;
  categoryLabel: string;
};

interface PropertyOwnershipSubBrickProps {
  title: string;
  subtitle: string | null;
  barGraphBars: BarGraphBar[];
  className?: string;
}

interface YAxisLeftTickProps {
  y: number;
  payload: { value: string };
}

const LeftAlignedTextYAxisTick = ({ y, payload }: YAxisLeftTickProps) => {
  return (
    <g transform={`translate(${0},${y})`}>
      <text x={0} y={0} textAnchor="start" dominantBaseline="middle" fontSize={14}>
        {payload.value}
      </text>
    </g>
  );
};

const CHART_HEIGHT = 110;

const ChartContainer = styled.div`
  height: ${CHART_HEIGHT}px;
`;

export const OwnershipSubBrick: FunctionComponent<PropertyOwnershipSubBrickProps> = ({
  title,
  subtitle,
  barGraphBars,
  className,
}) => {
  const theme = useTheme();

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const maximumBarValue = barGraphBars.reduce(
    (max, bar) => (bar.value > max ? bar.value : max),
    barGraphBars[0].value,
  );

  return (
    <ContentBrick
      titleComponent={<SubBrickTitle as="h3">{title}</SubBrickTitle>}
      subtitle={subtitle}
      isSubBrick={true}
      className={className}
    >
      <ChartContainer>
        {isClient && (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT} minWidth={160}>
            <BarChart data={barGraphBars} layout="vertical" barCategoryGap={3}>
              <XAxis type="number" hide domain={[0, maximumBarValue === 0 ? 1 : maximumBarValue]} />
              <YAxis
                dataKey="categoryLabel"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={LeftAlignedTextYAxisTick}
              />
              <Bar dataKey="value" name="barLabel" radius={3} minPointSize={46}>
                {barGraphBars.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      mapColorVariantToBarStyle(barGraphBars[index].colorVariant, theme).barColor
                    }
                  />
                ))}
                <LabelList
                  dataKey="barLabel"
                  // Custom label implementation required to control the colour of the label
                  content={({ x, y, width, value, index }) => {
                    if (typeof index !== 'number') return null;
                    const xValue = Number(x);
                    const yValue = Number(y);
                    const widthValue = Number(width);

                    return (
                      <g>
                        <text
                          fill={
                            mapColorVariantToBarStyle(barGraphBars[index].colorVariant, theme)
                              .barLabelColor
                          }
                          x={xValue + widthValue - 22}
                          y={yValue + 15}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {value}
                        </text>
                      </g>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartContainer>
    </ContentBrick>
  );
};
